import { UseCookieHook, CookieValue, UseCookieHookContext, useLogger } from "@amzn/xb-hooks";
import { PropsWithChildren, useReducer, useCallback } from "react";
import { CookiesProvider, useCookies } from "react-cookie";
import { useShortbread } from "../../hooks/useShortbread";

export const useCookie: UseCookieHook = (cookieName: string) => {
  const { log } = useLogger();
  const [shortbread, onConsentUpdated] = useShortbread();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [cookies, setCookie] = useCookies([cookieName]);

  const hasConsent = (name: string) => {
    try {
      return shortbread.hasConsent(name);
    } catch (error: unknown) {
      log.warn(`Error thrown inside of shortbread, likely due to attempting 
        to fetch a non-existent cookie: ${error}`);
      return false;
    }
  };

  // force update when consent updates
  onConsentUpdated(forceUpdate);

  // "cache" the functions to prevent consumers from rerendering
  const setter = useCallback(
    (value: CookieValue, options = {}) => (
      setCookie(cookieName, value, {
        ...options,
        secure: window.location.protocol.startsWith("https:"), // when secure, force secure
        sameSite: "strict",
      }),
      true
    ),
    [],
  );

  const noOpSetter = useCallback(() => false, []);

  if (hasConsent(cookieName)) {
    return [cookies[cookieName] ?? "", setter];
  }

  // null indicates the customer has opted out for this cookie
  return [null, noOpSetter];
};

export const UseCookieHookProvider = ({ children }: PropsWithChildren) => (
  <CookiesProvider defaultSetOptions={{ path: "/" }}>
    <UseCookieHookContext.Provider value={useCookie}>{children}</UseCookieHookContext.Provider>
  </CookiesProvider>
);
